import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
    writeBatch
} from 'firebase/firestore';
import { db } from './config';

export const CATEGORY_LEVELS = {
    MAIN: 'main',
    SUBCATEGORY: 'subcategory',
    SUBMENU: 'submenu',
};

const REQUIRED_FIELDS = ['name', 'status'];
const VALID_STATUS = ['active', 'inactive'];

function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function normalizeStatus(value) {
    return VALID_STATUS.includes(value) ? value : 'active';
}

function normalizeStringArray(values) {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    const cleaned = [];
    values.forEach((value) => {
        const v = normalizeString(value);
        if (!v || seen.has(v.toLowerCase())) return;
        seen.add(v.toLowerCase());
        cleaned.push(v);
    });
    return cleaned;
}

function normalizeSubcategoryDrafts(values) {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    return values.reduce((items, item) => {
        const draft = typeof item === 'string' ? { name: item } : item || {};
        const name = normalizeString(draft.name);
        if (!name || seen.has(name.toLowerCase())) return items;
        seen.add(name.toLowerCase());
        items.push({
            name,
            description: normalizeString(draft.description),
            imageUrl: normalizeString(draft.imageUrl),
            status: normalizeStatus(draft.status),
            submenus: normalizeSubmenuDrafts(draft.submenus),
        });
        return items;
    }, []);
}

function normalizeSubmenuDrafts(values) {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    return values.reduce((items, item) => {
        const draft = typeof item === 'string' ? { name: item } : item || {};
        const name = normalizeString(draft.name);
        if (!name || seen.has(name.toLowerCase())) return items;
        seen.add(name.toLowerCase());
        items.push({ name, status: normalizeStatus(draft.status) });
        return items;
    }, []);
}

function validateCategoryPayload(payload) {
    const missing = REQUIRED_FIELDS.filter((field) => !payload[field] && payload[field] !== 0);
    if (missing.length > 0) {
        return `Missing required fields: ${missing.join(', ')}`;
    }
    if (!VALID_STATUS.includes(payload.status)) {
        return 'Status must be either active or inactive.';
    }
    return null;
}

function legacySubcategoryNames(category) {
    if (Array.isArray(category.subcategories)) {
        return category.subcategories.filter((item) => typeof item === 'string' && item.trim());
    }
    return [];
}

function mergeLegacyHierarchy(categories) {
    const byId = new Map(categories.map((category) => [category.id, category]));
    const generated = [];

    categories.forEach((category) => {
        if (category.level && category.level !== CATEGORY_LEVELS.MAIN) return;
        legacySubcategoryNames(category).forEach((name) => {
            const exists = categories.some(
                (candidate) =>
                    candidate.level === CATEGORY_LEVELS.SUBCATEGORY &&
                    candidate.parentId === category.id &&
                    (candidate.name || '').toLowerCase() === name.toLowerCase()
            );
            if (!exists) {
                generated.push({
                    id: `legacy-${category.id}-${name.toLowerCase().replace(/\s+/g, '-')}`,
                    name,
                    description: '',
                    imageUrl: '',
                    status: category.status || 'active',
                    level: CATEGORY_LEVELS.SUBCATEGORY,
                    parentId: category.id,
                    parentName: category.name,
                    isLegacy: true,
                });
            }
        });
    });

    const merged = [...categories, ...generated].map((category) => {
        if (!category.parentId) return category;
        const parent = byId.get(category.parentId);
        return {
            ...category,
            parentName: category.parentName || parent?.name || '',
        };
    });

    return merged;
}

export function buildCategoryTree(categories = []) {
    const merged = mergeLegacyHierarchy(categories);
    const mainCategories = merged
        .filter((category) => !category.level || category.level === CATEGORY_LEVELS.MAIN)
        .map((category) => ({ ...category, level: CATEGORY_LEVELS.MAIN, subcategories: [] }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const mainById = new Map(mainCategories.map((category) => [category.id, category]));
    const subById = new Map();

    merged
        .filter((category) => category.level === CATEGORY_LEVELS.SUBCATEGORY)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .forEach((subcategory) => {
            const parent = mainById.get(subcategory.parentId);
            if (!parent) return;
            const item = { ...subcategory, submenus: [] };
            parent.subcategories.push(item);
            subById.set(subcategory.id, item);
        });

    merged
        .filter((category) => category.level === CATEGORY_LEVELS.SUBMENU)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .forEach((submenu) => {
            const parent = subById.get(submenu.parentId);
            if (!parent) return;
            parent.submenus.push(submenu);
        });

    return mainCategories;
}

export async function getAllCategories() {
    try {
        const categoriesRef = collection(db, 'categories');
        const q = query(categoriesRef, orderBy('name'));
        const snapshot = await getDocs(q);
        const categories = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        return { categories: mergeLegacyHierarchy(categories), error: null };
    } catch (err) {
        console.error('Error fetching categories:', err);
        return { categories: [], error: err.message };
    }
}

export async function getCategoryTree({ activeOnly = false } = {}) {
    const result = await getAllCategories();
    if (result.error) return { tree: [], categories: [], error: result.error };
    const categories = activeOnly
        ? result.categories.filter((category) => (category.status || 'active') === 'active')
        : result.categories;
    return { tree: buildCategoryTree(categories), categories, error: null };
}

export async function createCategory(categoryData) {
    const payload = {
        name: normalizeString(categoryData?.name),
        status: normalizeStatus(categoryData?.status),
        level: CATEGORY_LEVELS.MAIN,
        parentId: null,
        description: normalizeString(categoryData?.description),
        imageUrl: normalizeString(categoryData?.imageUrl),
        subcategories: normalizeSubcategoryDrafts(categoryData?.subcategories).map((item) => item.name),
    };

    const error = validateCategoryPayload(payload);
    if (error) return { categoryId: null, error };

    try {
        const batch = writeBatch(db);
        const categoryRef = doc(collection(db, 'categories'));
        batch.set(categoryRef, {
            ...payload,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        normalizeSubcategoryDrafts(categoryData?.subcategories).forEach((subcategory) => {
            const subRef = doc(collection(db, 'categories'));
            batch.set(subRef, {
                name: subcategory.name,
                description: subcategory.description,
                imageUrl: subcategory.imageUrl,
                status: subcategory.status,
                level: CATEGORY_LEVELS.SUBCATEGORY,
                parentId: categoryRef.id,
                parentName: payload.name,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            subcategory.submenus.forEach((submenu) => {
                const submenuRef = doc(collection(db, 'categories'));
                batch.set(submenuRef, {
                    name: submenu.name,
                    status: submenu.status,
                    level: CATEGORY_LEVELS.SUBMENU,
                    parentId: subRef.id,
                    parentName: subcategory.name,
                    mainCategoryId: categoryRef.id,
                    mainCategoryName: payload.name,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            });
        });

        await batch.commit();
        return { categoryId: categoryRef.id, error: null };
    } catch (err) {
        console.error('Error creating category:', err);
        return { categoryId: null, error: err.message };
    }
}

export async function updateCategory(categoryId, updates) {
    const payload = {};
    if (updates.name !== undefined) payload.name = normalizeString(updates.name);
    if (updates.status !== undefined) payload.status = normalizeStatus(updates.status);
    if (updates.description !== undefined) payload.description = normalizeString(updates.description);
    if (updates.imageUrl !== undefined) payload.imageUrl = normalizeString(updates.imageUrl);
    if (updates.parentId !== undefined) payload.parentId = updates.parentId || null;
    if (updates.parentName !== undefined) payload.parentName = normalizeString(updates.parentName);
    if (updates.subcategories !== undefined) payload.subcategories = normalizeStringArray(updates.subcategories);

    if (payload.name !== undefined && !payload.name) {
        return { success: false, error: 'Name is required.' };
    }
    if (payload.status && !VALID_STATUS.includes(payload.status)) {
        return { success: false, error: 'Status must be either active or inactive.' };
    }

    try {
        await updateDoc(doc(db, 'categories', categoryId), {
            ...payload,
            updatedAt: serverTimestamp(),
        });
        return { success: true, error: null };
    } catch (err) {
        console.error('Error updating category:', err);
        return { success: false, error: err.message };
    }
}

export async function deleteCategory(categoryId) {
    try {
        await deleteDoc(doc(db, 'categories', categoryId));
        return { success: true, error: null };
    } catch (err) {
        console.error('Error deleting category:', err);
        return { success: false, error: err.message };
    }
}

export async function createSubcategory(data) {
    const name = normalizeString(data?.name);
    const parentId = normalizeString(data?.parentId);
    if (!name || !parentId) return { categoryId: null, error: 'Subcategory name and parent main category are required.' };
    if (!normalizeString(data?.description)) return { categoryId: null, error: 'Description is required.' };
    try {
        const ref = await addDoc(collection(db, 'categories'), {
            name,
            description: normalizeString(data.description),
            imageUrl: normalizeString(data.imageUrl),
            status: normalizeStatus(data.status),
            level: CATEGORY_LEVELS.SUBCATEGORY,
            parentId,
            parentName: normalizeString(data.parentName),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        return { categoryId: ref.id, error: null };
    } catch (err) {
        console.error('Error creating subcategory:', err);
        return { categoryId: null, error: err.message };
    }
}

export async function createSubmenu(data) {
    const name = normalizeString(data?.name);
    const parentId = normalizeString(data?.parentId);
    if (!name || !parentId) return { categoryId: null, error: 'Submenu name and parent subcategory are required.' };
    try {
        const ref = await addDoc(collection(db, 'categories'), {
            name,
            status: normalizeStatus(data.status),
            level: CATEGORY_LEVELS.SUBMENU,
            parentId,
            parentName: normalizeString(data.parentName),
            mainCategoryId: normalizeString(data.mainCategoryId),
            mainCategoryName: normalizeString(data.mainCategoryName),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        return { categoryId: ref.id, error: null };
    } catch (err) {
        console.error('Error creating submenu:', err);
        return { categoryId: null, error: err.message };
    }
}

export async function replaceSubmenus(parentSubcategory, submenus = []) {
    if (!parentSubcategory?.id) return { success: false, error: 'Subcategory is required.' };
    try {
        const q = query(
            collection(db, 'categories'),
            where('level', '==', CATEGORY_LEVELS.SUBMENU),
            where('parentId', '==', parentSubcategory.id)
        );
        const snapshot = await getDocs(q);
        const batch = writeBatch(db);
        snapshot.docs.forEach((docSnap) => batch.delete(doc(db, 'categories', docSnap.id)));
        normalizeSubmenuDrafts(submenus).forEach((submenu) => {
            const ref = doc(collection(db, 'categories'));
            batch.set(ref, {
                name: submenu.name,
                status: submenu.status,
                level: CATEGORY_LEVELS.SUBMENU,
                parentId: parentSubcategory.id,
                parentName: parentSubcategory.name,
                mainCategoryId: parentSubcategory.parentId || '',
                mainCategoryName: parentSubcategory.parentName || '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        });
        await batch.commit();
        return { success: true, error: null };
    } catch (err) {
        console.error('Error replacing submenus:', err);
        return { success: false, error: err.message };
    }
}

export async function updateSubmenu(submenuId, updates) {
    try {
        const payload = {};
        if (updates.name !== undefined) payload.name = normalizeString(updates.name);
        if (updates.status !== undefined) payload.status = normalizeStatus(updates.status);
        if (updates.parentName !== undefined) payload.parentName = normalizeString(updates.parentName);
        
        if (payload.name !== undefined && !payload.name) {
            return { success: false, error: 'Name is required.' };
        }

        await updateDoc(doc(db, 'categories', submenuId), {
            ...payload,
            updatedAt: serverTimestamp(),
        });
        return { success: true, error: null };
    } catch (err) {
        console.error('Error updating submenu:', err);
        return { success: false, error: err.message };
    }
}

export async function deleteSubmenu(submenuId) {
    try {
        await deleteDoc(doc(db, 'categories', submenuId));
        return { success: true, error: null };
    } catch (err) {
        console.error('Error deleting submenu:', err);
        return { success: false, error: err.message };
    }
}

export async function ensureCategoriesExist(names = []) {
    const normalized = normalizeStringArray(names);
    if (normalized.length === 0) return { created: 0, error: null };

    const existing = await getAllCategories();
    if (existing.error) return { created: 0, error: existing.error };

    const existingNames = new Set(
        existing.categories
            .filter((category) => !category.level || category.level === CATEGORY_LEVELS.MAIN)
            .map((category) => (category.name || '').toLowerCase())
    );
    const toCreate = normalized.filter((name) => !existingNames.has(name.toLowerCase()));

    let created = 0;
    for (const name of toCreate) {
        const result = await createCategory({ name, status: 'active' });
        if (!result.error) created += 1;
    }

    return { created, error: null };
}
